import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import styled from 'styled-components';
import { FaBars, FaTimes } from 'react-icons/fa';
import { logoWhiteSimpleImage } from '../assets/images';

const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  const toggleMenu = () => {
    setIsOpen(!isOpen);
  };

  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 50) {
        setScrolled(true);
      } else {
        setScrolled(false);
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  useEffect(() => {
    setIsOpen(false);
  }, [location]);

  return (
    <NavContainer scrolled={scrolled}>
      <NavContent>
        <LogoContainer to="/">
          <Logo src={logoWhiteSimpleImage} alt="TK DJ Services Logo" />
          <LogoText>TK DJ SERVICES</LogoText>
        </LogoContainer>
        
        <MenuButton onClick={toggleMenu}>
          {isOpen ? <FaTimes /> : <FaBars />}
        </MenuButton>
        
        <NavLinks isOpen={isOpen}>
          <NavItem>
            <NavLink 
              to="/dj-services" 
              className={location.pathname === '/dj-services' ? 'active' : ''}
            >
              DJ Services
            </NavLink>
          </NavItem>
          <NavItem>
            <NavLink 
              to="/lighting-production" 
              className={location.pathname === '/lighting-production' ? 'active' : ''}
            >
              Lighting Production
            </NavLink>
          </NavItem>
          <NavItem>
            <NavLink 
              to="/vinyl-sales" 
              className={location.pathname === '/vinyl-sales' ? 'active' : ''}
            >
              Vinyl & Equipment
            </NavLink>
          </NavItem>
          <NavItem>
            <KeenAcousticLink 
              to="/keen-acoustic-sciences" 
              className={location.pathname === '/keen-acoustic-sciences' ? 'active' : ''}
            >
              Keen Acoustic Sciences
            </KeenAcousticLink>
          </NavItem>
        </NavLinks>
      </NavContent>
    </NavContainer>
  );
};

const NavContainer = styled.nav`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  z-index: 1000;
  background-color: ${({ scrolled }) => 
    scrolled ? 'rgba(19, 19, 19, 0.95)' : 'transparent'};
  transition: var(--transition);
  padding: ${({ scrolled }) => (scrolled ? '1rem 0' : '1.5rem 0')};
  box-shadow: ${({ scrolled }) => 
    scrolled ? '0 3px 10px rgba(193, 195, 143, 0.1), 0 2px 5px rgba(0, 0, 0, 0.08)' : 'none'};
  backdrop-filter: ${({ scrolled }) => (scrolled ? 'blur(10px)' : 'none')};
`;

const NavContent = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 90%;
  max-width: 1200px;
  margin: 0 auto;
`;

const LogoContainer = styled(Link)`
  display: flex;
  align-items: center;
  padding: 0.5rem 0;
`;

const Logo = styled.img`
  height: 65px;
  width: auto;
  transition: var(--transition);
`;

const LogoText = styled.span`
  color: var(--color-white);
  font-family: var(--font-accent);
  font-weight: 700;
  font-size: 1rem;
  margin-left: 1rem;
  letter-spacing: 1px;
  text-transform: uppercase;
  
  @media (max-width: 576px) {
    font-size: 0.8rem;
  }
`;

const MenuButton = styled.button`
  display: none;
  background: transparent;
  border: none;
  color: var(--color-tertiary);
  font-size: 1.5rem;
  cursor: pointer;
  z-index: 1001;

  @media (max-width: 768px) {
    display: block;
  }
`;

const NavLinks = styled.ul`
  display: flex;
  list-style: none;
  
  @media (max-width: 768px) {
    position: fixed;
    top: 0;
    right: 0;
    height: 100vh;
    width: 70%;
    max-width: 300px;
    flex-direction: column;
    background-color: var(--color-dark);
    padding: 7rem 2rem 2rem;
    transform: ${({ isOpen }) => 
      isOpen ? 'translateX(0)' : 'translateX(100%)'};
    transition: transform 0.3s ease-in-out;
    box-shadow: ${({ isOpen }) => 
      isOpen ? '-5px 0 15px rgba(0, 0, 0, 0.2)' : 'none'};
    z-index: 1000;
  }
`;

const NavItem = styled.li`
  margin: 0 1rem;
  
  @media (max-width: 768px) {
    margin: 1rem 0;
  }
`;

const NavLink = styled(Link)`
  color: var(--color-tertiary);
  font-family: var(--font-accent);
  font-weight: 500;
  position: relative;
  padding: 0.75rem 0;
  font-size: 0.95rem;
  letter-spacing: 0.5px;
  
  &:after {
    content: '';
    position: absolute;
    bottom: 0;
    left: 0;
    width: 0;
    height: 2px;
    background-color: var(--color-secondary);
    transition: width 0.3s ease;
  }
  
  &:hover:after, &.active:after {
    width: 100%;
  }
  
  &.active {
    color: var(--color-secondary);
  }
  
  @media (max-width: 768px) {
    font-size: 1rem;
    padding: 0.5rem 0;
  }
`;

const KeenAcousticLink = styled(NavLink)`
  color: var(--color-secondary);
  font-weight: 600;
  text-shadow: 0 0 10px rgba(193, 195, 143, 0.4);
  
  &:after {
    background-color: var(--color-secondary);
  }
  
  &.active {
    color: var(--color-secondary);
    text-shadow: 0 0 12px rgba(193, 195, 143, 0.6);
  }
  
  &:hover {
    text-shadow: 0 0 12px rgba(193, 195, 143, 0.7);
  }
`;

export default Navbar; 